import React, { useState, useEffect, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { YearlyGoal } from '../../types';
import {
  Target,
  Plus,
  Search,
  Trash2,
  Edit3,
  X,
  Check,
  CheckCircle2,
  Circle,
  Calendar,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

export const GoalsView: React.FC = () => {
  const {
    yearlyGoals,
    addYearlyGoal,
    editYearlyGoal,
    deleteYearlyGoal,
    triggerConfetti,
  } = useApp();

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isCompletedExpanded, setIsCompletedExpanded] = useState<boolean>(true);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<YearlyGoal | null>(null);

  // Form State - Just Title and Details
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formCompleted, setFormCompleted] = useState(false);

  // Handle Escape key to close modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) {
        setIsModalOpen(false);
        setEditingGoal(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen]);

  // Open modal for Create
  const handleOpenCreate = () => {
    setEditingGoal(null);
    setFormTitle('');
    setFormDescription('');
    setFormCompleted(false);
    setIsModalOpen(true);
  };

  // Open modal for Edit
  const handleOpenEdit = (goal: YearlyGoal) => {
    setEditingGoal(goal);
    setFormTitle(goal.title);
    setFormDescription(goal.description || '');
    setFormCompleted(goal.status === 'completed');
    setIsModalOpen(true);
  };

  // Save Goal
  const handleSubmitForm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle.trim()) return;

    const status = formCompleted ? 'completed' : 'in_progress';
    const progress = formCompleted ? 100 : 0;

    if (editingGoal) {
      editYearlyGoal(editingGoal.id, {
        title: formTitle.trim(),
        description: formDescription.trim(),
        category: 'personal',
        status,
        progress,
        milestones: [],
      });
    } else {
      addYearlyGoal({
        year: selectedYear,
        title: formTitle.trim(),
        description: formDescription.trim(),
        category: 'personal',
        status,
        progress,
        milestones: [],
      });
    }

    setIsModalOpen(false);
    setEditingGoal(null);
  };

  // Toggle Goal Completed / In Progress
  const handleToggleGoalStatus = (goal: YearlyGoal) => {
    const isCompleted = goal.status === 'completed';
    if (!isCompleted) {
      triggerConfetti();
    }
    editYearlyGoal(goal.id, {
      status: isCompleted ? 'in_progress' : 'completed',
      progress: isCompleted ? 0 : 100,
      completedAt: isCompleted ? undefined : new Date().toISOString(),
    });
  };

  // Available Years
  const availableYears = useMemo(() => {
    const years = new Set<number>([currentYear - 1, currentYear, currentYear + 1]);
    (yearlyGoals || []).forEach((g) => {
      if (g.year) years.add(g.year);
    });
    return Array.from(years).sort((a, b) => a - b);
  }, [yearlyGoals, currentYear]);

  // Filtered Goals by Year & Search Query
  const filteredGoals = useMemo(() => {
    return (yearlyGoals || []).filter((g) => {
      if (g.year && g.year !== selectedYear) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchTitle = g.title.toLowerCase().includes(q);
        const matchDesc = g.description?.toLowerCase().includes(q);
        return matchTitle || matchDesc;
      }
      return true;
    });
  }, [yearlyGoals, selectedYear, searchQuery]);

  // Split into In-Progress (Top) and Completed (Bottom)
  const inProgressGoals = useMemo(
    () => filteredGoals.filter((g) => g.status !== 'completed'),
    [filteredGoals]
  );
  const completedGoals = useMemo(
    () => filteredGoals.filter((g) => g.status === 'completed'),
    [filteredGoals]
  );

  const completedCount = completedGoals.length;
  const totalCount = filteredGoals.length;

  const renderGoalCard = (goal: YearlyGoal) => {
    const isCompleted = goal.status === 'completed';

    return (
      <div
        key={goal.id}
        className={`bg-white dark:bg-[#111827] border rounded-3xl p-5 shadow-xs flex flex-col justify-between min-h-[170px] transition-all hover:shadow-md ${
          isCompleted
            ? 'border-emerald-500/30 bg-emerald-500/[0.02] opacity-80'
            : 'border-slate-200/80 dark:border-slate-800 hover:border-emerald-500/50'
        }`}
      >
        <div>
          {/* Top Status & Action Bar */}
          <div className="flex items-center justify-between gap-2 mb-2.5">
            <button
              onClick={() => handleToggleGoalStatus(goal)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl text-xs font-bold transition-all ${
                isCompleted
                  ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:text-emerald-500'
              }`}
              title={isCompleted ? 'Mark as In Progress' : 'Mark as Completed'}
            >
              {isCompleted ? (
                <>
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  <span>Completed</span>
                </>
              ) : (
                <>
                  <Circle className="w-3.5 h-3.5" />
                  <span>In Progress</span>
                </>
              )}
            </button>

            <div className="flex items-center gap-1">
              <button
                onClick={() => handleOpenEdit(goal)}
                className="p-1.5 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                title="Edit Goal"
              >
                <Edit3 className="w-3.5 h-3.5" />
              </button>

              <button
                onClick={() => deleteYearlyGoal(goal.id)}
                className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl transition-colors"
                title="Delete Goal"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Title */}
          <h3
            className={`text-base font-bold leading-snug break-words mb-2 ${
              isCompleted
                ? 'line-through text-slate-400 dark:text-slate-500'
                : 'text-slate-900 dark:text-white'
            }`}
          >
            {goal.title}
          </h3>

          {/* Details */}
          {goal.description && (
            <p className="text-xs text-slate-600 dark:text-slate-400 whitespace-pre-wrap leading-relaxed">
              {goal.description}
            </p>
          )}
        </div>

        {/* Footer Date / Year */}
        <div className="pt-3 border-t border-slate-100 dark:border-slate-800/80 mt-4 flex items-center justify-between text-[11px] text-slate-400 font-mono">
          <span>Year {goal.year || selectedYear}</span>
          {goal.completedAt && (
            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
              Done {new Date(goal.completedAt).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in pb-16">
      {/* Header with Title, Year Selector & New Goal Button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-2xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
              <Target className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
                <span>Goals & Plans</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-bold">
                  {completedCount}/{totalCount} completed
                </span>
              </h1>
            </div>
          </div>
        </div>

        {/* Year Selector & Add Button */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl border border-slate-200/80 dark:border-slate-700">
            {availableYears.map((yr) => (
              <button
                key={yr}
                onClick={() => setSelectedYear(yr)}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  selectedYear === yr
                    ? 'bg-white dark:bg-[#111827] text-emerald-600 dark:text-emerald-400 shadow-xs'
                    : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {yr}
              </button>
            ))}
          </div>

          <button
            onClick={handleOpenCreate}
            className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-500/25 flex items-center gap-1.5 transition-all transform active:scale-95"
          >
            <Plus className="w-4 h-4" />
            <span>New Goal</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="p-3 bg-white dark:bg-[#111827] border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-xs">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search goals & plans..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-8 py-2 bg-slate-100 dark:bg-slate-800 border border-transparent rounded-xl text-xs text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-white"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Goals List (Simple Box Cards with Title & Details) */}
      {filteredGoals.length === 0 ? (
        <div className="p-12 text-center bg-white dark:bg-[#111827] rounded-3xl border border-slate-200/80 dark:border-slate-800 shadow-xs space-y-3">
          <div className="w-14 h-14 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center mx-auto">
            <Target className="w-7 h-7" />
          </div>
          <h3 className="text-base font-black text-slate-900 dark:text-white">
            {searchQuery ? 'No matching goals found' : `No goals set for ${selectedYear}`}
          </h3>
          <p className="text-xs text-slate-500 max-w-sm mx-auto">
            {searchQuery
              ? 'Try searching with different keywords.'
              : 'Add your goals and plans with a title and details!'}
          </p>
          <button
            onClick={handleOpenCreate}
            className="mt-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-bold transition-colors"
          >
            + Create Goal for {selectedYear}
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {/* IN PROGRESS GOALS (Top Section) */}
          {inProgressGoals.length > 0 && (
            <div className="space-y-3">
              {completedGoals.length > 0 && (
                <div className="flex items-center gap-2 px-1">
                  <Circle className="w-3.5 h-3.5 text-emerald-500" />
                  <h2 className="text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    In Progress ({inProgressGoals.length})
                  </h2>
                  <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {inProgressGoals.map((goal) => renderGoalCard(goal))}
              </div>
            </div>
          )}

          {/* COMPLETED GOALS (Bottom Section - Auto moved down) */}
          {completedGoals.length > 0 && (
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                  <h2 className="text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Completed ({completedGoals.length})
                  </h2>
                </div>

                <button
                  onClick={() => setIsCompletedExpanded(!isCompletedExpanded)}
                  className="text-xs font-bold text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 flex items-center gap-1"
                >
                  <span>{isCompletedExpanded ? 'Hide' : 'Show'}</span>
                  {isCompletedExpanded ? (
                    <ChevronUp className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronDown className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>

              <div className="h-px bg-slate-200 dark:bg-slate-800" />

              {isCompletedExpanded && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                  {completedGoals.map((goal) => renderGoalCard(goal))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Goal Creator / Editor Modal - Simply Title and Details */}
      {isModalOpen && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setIsModalOpen(false);
              setEditingGoal(null);
            }
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in"
        >
          <div className="w-full max-w-lg bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-3xl shadow-2xl overflow-hidden animate-scale-in">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center">
                  <Target className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  {editingGoal ? 'Edit Goal' : 'New Goal'}
                </h3>
              </div>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setEditingGoal(null);
                }}
                className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white rounded-xl"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmitForm} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  placeholder="e.g. Master Cloudflare Workers & Serverless Architecture"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-sm font-bold text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1">
                  Details
                </label>
                <textarea
                  rows={5}
                  placeholder="Write your goal details, notes, or execution plan here..."
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl text-xs sm:text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none font-sans leading-relaxed"
                />
              </div>

              {/* Status Toggle */}
              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setFormCompleted(!formCompleted)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold border transition-all ${
                    formCompleted
                      ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-transparent hover:text-emerald-500'
                  }`}
                >
                  {formCompleted ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  ) : (
                    <Circle className="w-4 h-4" />
                  )}
                  <span>{formCompleted ? 'Goal Completed' : 'Mark as Completed'}</span>
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingGoal(null);
                  }}
                  className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white rounded-xl text-xs font-bold shadow-md shadow-emerald-500/25 flex items-center gap-1.5 transition-all transform active:scale-95"
                >
                  <Check className="w-4 h-4" />
                  <span>Save Goal</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
